import React, { useState, useCallback } from 'react';
import { CustomDialog } from '../components/CustomDialog';

interface DialogOptions {
  title?: string;
  type?: 'info' | 'success' | 'error' | 'warning' | 'confirm';
  confirmText?: string;
  cancelText?: string;
}

interface DialogState {
  isOpen: boolean;
  message: string;
  options: DialogOptions;
  resolve?: (value: boolean) => void;
}

export const useDialog = () => {
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    message: '',
    options: {},
  });

  const showAlert = useCallback((message: string, options: DialogOptions = {}) => {
    return new Promise<void>((resolve) => {
      setDialogState({
        isOpen: true,
        message,
        options: {
          type: options.type || 'info',
          title: options.title,
          confirmText: options.confirmText || 'OK',
        },
        resolve: () => {
          resolve();
          return true;
        },
      });
    });
  }, []);

  const showConfirm = useCallback((message: string, options: DialogOptions = {}) => {
    return new Promise<boolean>((resolve) => {
      setDialogState({
        isOpen: true,
        message,
        options: {
          type: options.type || 'confirm',
          title: options.title,
          confirmText: options.confirmText || 'Да',
          cancelText: options.cancelText || 'Отмена',
        },
        resolve,
      });
    });
  }, []);

  const handleClose = useCallback(() => {
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    dialogState.resolve?.(true);
    handleClose();
  }, [dialogState.resolve, handleClose]);

  const handleCancel = useCallback(() => {
    dialogState.resolve?.(false);
    handleClose();
  }, [dialogState.resolve, handleClose]);

  const DialogComponent = useCallback(() => {
    return (
      <CustomDialog
        isOpen={dialogState.isOpen}
        onClose={handleClose}
        message={dialogState.message}
        title={dialogState.options.title}
        type={dialogState.options.type}
        confirmText={dialogState.options.confirmText}
        cancelText={dialogState.options.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        showCancel={dialogState.options.type === 'confirm'}
      />
    );
  }, [dialogState, handleClose, handleConfirm, handleCancel]);

  return {
    showAlert,
    showConfirm,
    DialogComponent,
  };
};

